import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  CTAGroup,
  ReorderButtons,
  SelectInput,
} from "../../../components/FormField";

import { MediaImageField } from "../../../components/MediaPickerDialog";
import { emptyButton, moveItem } from "./shared";
import { ICON_OPTIONS } from "../../../lib/iconsData";

export function ServicesSections({ setSectionsData, initialData }) {
  /* ---------------- HERO ---------------- */
  const [heroActive, setHeroActive] = useState(true);
  const [heroBadgeText, setHeroBadgeText] = useState("Services");
  const [heroTitle, setHeroTitle] = useState("Comprehensive Business");
  const [heroHighlight, setHeroHighlight] = useState("Technology Services");
  const [heroSubline, setHeroSubline] = useState("");
  const [heroVideo, setHeroVideo] = useState("");
  const [heroPrimary, setHeroPrimary] = useState(emptyButton());
  const [heroSecondary, setHeroSecondary] = useState({
    text: "Explore Our Services",
    url: "#our-services",
    newTab: false,
  });
  const [trustBadges, setTrustBadges] = useState([
    { icon: "Award", title: "SAP Partner", sub: "Certified Experts" },
    { icon: "Shield", title: "150+ Projects", sub: "Delivered Successfully" },
    { icon: "Users", title: "60+ Years", sub: "Combined Experience" },
  ]);

  /* ---------------- SERVICES SNAPSHOT ---------------- */
  const [snapActive, setSnapActive] = useState(true);
  const [snapBadgeText, setSnapBadgeText] = useState("Services Snapshot");
  const [snapHeading, setSnapHeading] = useState("Empowering");
  const [snapHeadingEmphasis, setSnapHeadingEmphasis] =
    useState("Business Growth");
  const [snapDescription, setSnapDescription] = useState("");
  const [snapCards, setSnapCards] = useState([
    {
      icon: "Rocket",
      title: "Fast Deployment",
      description: "Structured rollouts with minimal disruption",
    },
    {
      icon: "Cog",
      title: "Seamless Integration",
      description: "Connect all your systems effortlessly",
    },
    {
      icon: "Headphones",
      title: "Ongoing Support",
      description: "Local expertise when you need it",
    },
  ]);
  const [snapCTAText, setSnapCTAText] = useState("See Our Services");
  const [snapCTALink, setSnapCTALink] = useState("#our-services");

  /* ---------------- CORE SERVICES ---------------- */
  const [coreActive, setCoreActive] = useState(true);
  const [coreBadgeText, setCoreBadgeText] = useState("Core Services");
  const [coreHeading, setCoreHeading] = useState("Our Core");
  const [coreHeadingEmphasis, setCoreHeadingEmphasis] = useState("Services");
  const [coreDescription, setCoreDescription] = useState(
    "We guide you through every stage of your digital transformation journey.",
  );
  const [serviceCategories, setServiceCategories] = useState([
    {
      title: "",
      description: "",
      icon: "",
      services: [{ title: "", href: "" }],
    },
  ]);
  const [coreCtaText, setCoreCtaText] = useState("Explore All Services");
  const [coreCtaLink, setCoreCtaLink] = useState("#our-services");

  /* ---------------- HOW WE HELP ---------------- */
  const [howActive, setHowActive] = useState(true);
  const [howBadgeText, setHowBadgeText] = useState("How We Help");
  const [howHeading, setHowHeading] = useState("How Our Services Drive");
  const [howHeadingEmphasis, setHowHeadingEmphasis] = useState("Success");
  const [howDescription, setHowDescription] = useState(
    "Tailored solutions for mid-market businesses seeking growth and efficiency.",
  );
  const [howCards, setHowCards] = useState([{ title: "", description: "" }]);
  const [howCtaText, setHowCtaText] = useState("Request a Demo");
  const [howCtaLink, setHowCtaLink] = useState("/contact");

  /* ---------------- TECHNOLOGY ---------------- */
  const [techActive, setTechActive] = useState(true);
  const [techBadgeText, setTechBadgeText] = useState("Technology");
  const [techHeading, setTechHeading] = useState("Technology Behind Our");
  const [techHeadingEmphasis, setTechHeadingEmphasis] = useState("Services");
  const [techDescription, setTechDescription] = useState("");
  const [techIntegrations, setTechIntegrations] = useState([
    "SAP Business One",
    "Salesforce",
    "SAP BTP",
    "Power BI",
  ]);
  const [techCtaText, setTechCtaText] = useState("Explore Products");
  const [techCtaLink, setTechCtaLink] = useState("/products");

  /* ---------------- CLIENT SUCCESS ---------------- */
  const [successActive, setSuccessActive] = useState(true);
  const [successBadgeText, setSuccessBadgeText] = useState("Client Success");
  const [successHeading, setSuccessHeading] = useState("Client Success");
  const [successHeadingEmphasis, setSuccessHeadingEmphasis] = useState("Story");
  const [successQuote, setSuccessQuote] = useState("");
  const [successAuthor, setSuccessAuthor] = useState("");
  const [successKpis, setSuccessKpis] = useState([
    "35% efficiency increase",
    "95% system uptime",
    "30% faster processes",
  ]);
  const [successCtaText, setSuccessCtaText] = useState(
    "View More Case Studies",
  );
  const [successCtaLink, setSuccessCtaLink] = useState(
    "/resources/case-studies",
  );

  /* ---------------- WHY THYNKPROCESS ---------------- */
  const [whyActive, setWhyActive] = useState(true);
  const [whyBadgeText, setWhyBadgeText] = useState("Why ThynkProcess");
  const [whyHeading, setWhyHeading] = useState("Your Strategic");
  const [whyHeadingEmphasis, setWhyHeadingEmphasis] = useState("Partner");
  const [whyCards, setWhyCards] = useState([{ title: "", description: "" }]);

  /* ---------------- FINAL CTA ---------------- */
  const [ctaActive, setCtaActive] = useState(true);
  const [ctaHeading, setCtaHeading] = useState(
    "Transform Your Business with ThynkProcess",
  );
  const [ctaDescription, setCtaDescription] = useState("");
  const [ctaPrimary, setCtaPrimary] = useState(emptyButton());
  const [ctaSecondary, setCtaSecondary] = useState({
    text: "Explore Services",
    url: "#our-services",
    newTab: false,
  });
  const [ctaContact, setCtaContact] = useState(
    "+61 3 7075 3900 | info@thynkprocess.com.au | South Melbourne, Victoria",
  );

  const updateBtn = (setter, key, val) => setter((b) => ({ ...b, [key]: val }));

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;

    if (d.hero) {
      setHeroBadgeText(d.hero.badge_text || "");
      setHeroTitle(d.hero.title || "");
      setHeroHighlight(d.hero.highlight || "");
      setHeroSubline(d.hero.subline || "");
      setHeroVideo(d.hero.video || "");
      if (d.hero.primary_cta)
        setHeroPrimary({
          text: d.hero.primary_cta.text || "",
          url: d.hero.primary_cta.link || "",
          newTab: false,
        });
      if (d.hero.secondary_cta)
        setHeroSecondary({
          text: d.hero.secondary_cta.text || "",
          url: d.hero.secondary_cta.link || "",
          newTab: false,
        });
      if (d.hero.trust_badges?.length) setTrustBadges(d.hero.trust_badges);
    }

    if (d.services_snapshot) {
      setSnapBadgeText(d.services_snapshot.badge_text || "");
      setSnapHeading(d.services_snapshot.heading || "");
      setSnapHeadingEmphasis(d.services_snapshot.heading_emphasis || "");
      setSnapDescription(d.services_snapshot.description || "");
      if (d.services_snapshot.cards?.length)
        setSnapCards(d.services_snapshot.cards);
      setSnapCTAText(d.services_snapshot.cta_text || "");
      setSnapCTALink(d.services_snapshot.cta_link || "");
    }

    if (d.core_services) {
      setCoreBadgeText(d.core_services.badge_text || "");
      setCoreHeading(d.core_services.heading || "");
      setCoreHeadingEmphasis(d.core_services.heading_emphasis || "");
      setCoreDescription(d.core_services.description || "");
      if (d.core_services.categories?.length)
        setServiceCategories(d.core_services.categories);
      setCoreCtaText(d.core_services.cta_text || "");
      setCoreCtaLink(d.core_services.cta_link || "");
    }

    if (d.how_we_help) {
      setHowBadgeText(d.how_we_help.badge_text || "");
      setHowHeading(d.how_we_help.heading || "");
      setHowHeadingEmphasis(d.how_we_help.heading_emphasis || "");
      setHowDescription(d.how_we_help.description || "");
      if (d.how_we_help.cards?.length) setHowCards(d.how_we_help.cards);
      setHowCtaText(d.how_we_help.cta_text || "");
      setHowCtaLink(d.how_we_help.cta_link || "");
    }

    if (d.technology) {
      setTechBadgeText(d.technology.badge_text || "");
      setTechHeading(d.technology.heading || "");
      setTechHeadingEmphasis(d.technology.heading_emphasis || "");
      setTechDescription(d.technology.description || "");
      if (d.technology.integrations?.length)
        setTechIntegrations(d.technology.integrations);
      setTechCtaText(d.technology.cta_text || "");
      setTechCtaLink(d.technology.cta_link || "");
    }

    if (d.client_success) {
      setSuccessBadgeText(d.client_success.badge_text || "");
      setSuccessHeading(d.client_success.heading || "");
      setSuccessHeadingEmphasis(d.client_success.heading_emphasis || "");
      setSuccessQuote(d.client_success.quote || "");
      setSuccessAuthor(d.client_success.author || "");
      if (d.client_success.kpis?.length) setSuccessKpis(d.client_success.kpis);
      setSuccessCtaText(d.client_success.cta_text || "");
      setSuccessCtaLink(d.client_success.cta_link || "");
    }

    if (d.why_thynkprocess) {
      setWhyBadgeText(d.why_thynkprocess.badge_text || "");
      setWhyHeading(d.why_thynkprocess.heading || "");
      setWhyHeadingEmphasis(d.why_thynkprocess.heading_emphasis || "");
      if (d.why_thynkprocess.cards?.length)
        setWhyCards(d.why_thynkprocess.cards);
    }

    if (d.final_cta) {
      setCtaHeading(d.final_cta.heading || "");
      setCtaDescription(d.final_cta.description || "");
      setCtaContact(d.final_cta.contact || "");
      if (d.final_cta.primary_cta)
        setCtaPrimary({
          text: d.final_cta.primary_cta.text || "",
          url: d.final_cta.primary_cta.link || "",
          newTab: false,
        });
      if (d.final_cta.secondary_cta)
        setCtaSecondary({
          text: d.final_cta.secondary_cta.text || "",
          url: d.final_cta.secondary_cta.link || "",
          newTab: false,
        });
    }
  }, [initialData]);

  /* ---------------- BUILD PAYLOAD ---------------- */
  const buildPayload = () => ({
    hero: {
      badge_text: heroBadgeText,
      title: heroTitle,
      highlight: heroHighlight,
      subline: heroSubline,
      video: heroVideo,
      primary_cta: { text: heroPrimary.text, link: heroPrimary.url },
      secondary_cta: { text: heroSecondary.text, link: heroSecondary.url },
      trust_badges: trustBadges,
    },
    services_snapshot: {
      badge_text: snapBadgeText,
      heading: snapHeading,
      heading_emphasis: snapHeadingEmphasis,
      description: snapDescription,
      cards: snapCards,
      cta_text: snapCTAText,
      cta_link: snapCTALink,
    },
    core_services: {
      badge_text: coreBadgeText,
      heading: coreHeading,
      heading_emphasis: coreHeadingEmphasis,
      description: coreDescription,
      categories: serviceCategories,
      cta_text: coreCtaText,
      cta_link: coreCtaLink,
    },
    how_we_help: {
      badge_text: howBadgeText,
      heading: howHeading,
      heading_emphasis: howHeadingEmphasis,
      description: howDescription,
      cards: howCards,
      cta_text: howCtaText,
      cta_link: howCtaLink,
    },
    technology: {
      badge_text: techBadgeText,
      heading: techHeading,
      heading_emphasis: techHeadingEmphasis,
      description: techDescription,
      integrations: techIntegrations,
      cta_text: techCtaText,
      cta_link: techCtaLink,
    },
    client_success: {
      badge_text: successBadgeText,
      heading: successHeading,
      heading_emphasis: successHeadingEmphasis,
      quote: successQuote,
      author: successAuthor,
      kpis: successKpis,
      cta_text: successCtaText,
      cta_link: successCtaLink,
    },
    why_thynkprocess: {
      badge_text: whyBadgeText,
      heading: whyHeading,
      heading_emphasis: whyHeadingEmphasis,
      cards: whyCards,
    },
    final_cta: {
      heading: ctaHeading,
      description: ctaDescription,
      contact: ctaContact,
      primary_cta: { text: ctaPrimary.text, link: ctaPrimary.url },
      secondary_cta: { text: ctaSecondary.text, link: ctaSecondary.url },
    },
  });

  useEffect(() => {
    setSectionsData?.(buildPayload());
  }, [
    heroBadgeText,
    heroTitle,
    heroHighlight,
    heroSubline,
    heroVideo,
    heroPrimary,
    heroSecondary,
    trustBadges,
    snapBadgeText,
    snapHeading,
    snapHeadingEmphasis,
    snapDescription,
    snapCards,
    snapCTAText,
    snapCTALink,
    coreBadgeText,
    coreHeading,
    coreHeadingEmphasis,
    coreDescription,
    serviceCategories,
    coreCtaText,
    coreCtaLink,
    howBadgeText,
    howHeading,
    howHeadingEmphasis,
    howDescription,
    howCards,
    howCtaText,
    howCtaLink,
    techBadgeText,
    techHeading,
    techHeadingEmphasis,
    techDescription,
    techIntegrations,
    techCtaText,
    techCtaLink,
    successBadgeText,
    successHeading,
    successHeadingEmphasis,
    successQuote,
    successAuthor,
    successKpis,
    successCtaText,
    successCtaLink,
    whyBadgeText,
    whyHeading,
    whyHeadingEmphasis,
    whyCards,
    ctaHeading,
    ctaDescription,
    ctaContact,
    ctaPrimary,
    ctaSecondary,
  ]);

  /* ---------------- RENDER ---------------- */
  return (
    <>
      {/* HERO */}
      <CollapsibleSection
        title="Hero Section"
        active={heroActive}
        onToggleActive={setHeroActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={heroBadgeText}
            onChange={(e) => setHeroBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Title">
          <TextInput
            value={heroTitle}
            onChange={(e) => setHeroTitle(e.target.value)}
          />
        </Field>
        <Field label="Highlight (accent colour)">
          <TextInput
            value={heroHighlight}
            onChange={(e) => setHeroHighlight(e.target.value)}
          />
        </Field>
        <Field label="Subline">
          <TextArea
            value={heroSubline}
            onChange={(e) => setHeroSubline(e.target.value)}
          />
        </Field>
        <Field label="Hero Video">
          <MediaImageField
            label="Select Hero Video"
            value={heroVideo}
            onChange={setHeroVideo}
          />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={heroPrimary.text}
          url={heroPrimary.url}
          newTab={heroPrimary.newTab}
          onTextChange={(v) => updateBtn(setHeroPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setHeroPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setHeroPrimary, "newTab", v)}
        />
        <CTAGroup
          label="Secondary CTA"
          text={heroSecondary.text}
          url={heroSecondary.url}
          newTab={heroSecondary.newTab}
          onTextChange={(v) => updateBtn(setHeroSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setHeroSecondary, "url", v)}
          onNewTabChange={(v) => updateBtn(setHeroSecondary, "newTab", v)}
        />

        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Trust Badges</p>
          <button
            onClick={() =>
              setTrustBadges((s) => [...s, { icon: "", title: "", sub: "" }])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Badge
          </button>
        </div>
        {trustBadges.map((b, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Badge {i + 1}</p>
              {trustBadges.length > 1 && (
                <button
                  onClick={() =>
                    setTrustBadges((s) => s.filter((_, idx) => idx !== i))
                  }
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <Field label="Icon">
              <SelectInput
                value={b.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setTrustBadges((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Title">
                <TextInput
                  value={b.title}
                  onChange={(e) =>
                    setTrustBadges((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, title: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="Sub">
                <TextInput
                  value={b.sub}
                  onChange={(e) =>
                    setTrustBadges((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, sub: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* SERVICES SNAPSHOT */}
      <CollapsibleSection
        title="Services Snapshot"
        active={snapActive}
        onToggleActive={setSnapActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={snapBadgeText}
            onChange={(e) => setSnapBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={snapHeading}
            onChange={(e) => setSnapHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={snapHeadingEmphasis}
            onChange={(e) => setSnapHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={snapDescription}
            onChange={(e) => setSnapDescription(e.target.value)}
          />
        </Field>

        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Snapshot Cards</p>
          <button
            onClick={() =>
              setSnapCards((s) => [
                ...s,
                { icon: "", title: "", description: "" },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Card
          </button>
        </div>
        {snapCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Card {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={snapCards.length}
                  onMove={(f, t) => setSnapCards(moveItem(snapCards, f, t))}
                />
                {snapCards.length > 1 && (
                  <button
                    onClick={() =>
                      setSnapCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Icon">
              <SelectInput
                value={c.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setSnapCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setSnapCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextInput
                value={c.description}
                onChange={(e) =>
                  setSnapCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="CTA Text">
            <TextInput
              value={snapCTAText}
              onChange={(e) => setSnapCTAText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={snapCTALink}
              onChange={(e) => setSnapCTALink(e.target.value)}
            />
          </Field>
        </div>
      </CollapsibleSection>

      {/* CORE SERVICES */}
      <CollapsibleSection
        title="Core Services"
        active={coreActive}
        onToggleActive={setCoreActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={coreBadgeText}
            onChange={(e) => setCoreBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={coreHeading}
            onChange={(e) => setCoreHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={coreHeadingEmphasis}
            onChange={(e) => setCoreHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={coreDescription}
            onChange={(e) => setCoreDescription(e.target.value)}
          />
        </Field>

        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Service Categories</p>
          <button
            onClick={() =>
              setServiceCategories((s) => [
                ...s,
                {
                  title: "",
                  description: "",
                  icon: "",
                  services: [{ title: "", href: "" }],
                },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Category
          </button>
        </div>
        {serviceCategories.map((cat, ci) => (
          <div key={ci} className="border p-3 rounded-lg mb-4 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Category {ci + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={ci}
                  total={serviceCategories.length}
                  onMove={(f, t) =>
                    setServiceCategories(moveItem(serviceCategories, f, t))
                  }
                />
                {serviceCategories.length > 1 && (
                  <button
                    onClick={() =>
                      setServiceCategories((s) =>
                        s.filter((_, idx) => idx !== ci),
                      )
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={cat.title}
                onChange={(e) =>
                  setServiceCategories((s) =>
                    s.map((x, idx) =>
                      idx === ci ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={cat.description}
                onChange={(e) =>
                  setServiceCategories((s) =>
                    s.map((x, idx) =>
                      idx === ci ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Icon">
              <SelectInput
                value={cat.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setServiceCategories((s) =>
                    s.map((x, idx) =>
                      idx === ci ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>

            <div className="flex justify-between mt-3 mb-1">
              <p className="text-xs font-semibold">Service Links</p>
              <button
                onClick={() =>
                  setServiceCategories((s) =>
                    s.map((x, idx) =>
                      idx === ci
                        ? {
                            ...x,
                            services: [...x.services, { title: "", href: "" }],
                          }
                        : x,
                    ),
                  )
                }
                className="flex items-center gap-1 text-xs"
              >
                <Plus size={14} /> Add Service
              </button>
            </div>
            {cat.services.map((svc, si) => (
              <div
                key={si}
                className="border border-dashed p-3 rounded-lg mb-2 space-y-2 ml-2"
              >
                <div className="flex justify-between">
                  <p className="text-xs font-semibold text-muted-foreground">
                    Service {si + 1}
                  </p>
                  <div className="flex gap-2">
                    <ReorderButtons
                      index={si}
                      total={cat.services.length}
                      onMove={(f, t) =>
                        setServiceCategories((s) =>
                          s.map((x, idx) =>
                            idx === ci
                              ? { ...x, services: moveItem(x.services, f, t) }
                              : x,
                          ),
                        )
                      }
                    />
                    {cat.services.length > 1 && (
                      <button
                        onClick={() =>
                          setServiceCategories((s) =>
                            s.map((x, idx) =>
                              idx === ci
                                ? {
                                    ...x,
                                    services: x.services.filter(
                                      (_, sidx) => sidx !== si,
                                    ),
                                  }
                                : x,
                            ),
                          )
                        }
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <Field label="Title">
                  <TextInput
                    value={svc.title}
                    onChange={(e) =>
                      setServiceCategories((s) =>
                        s.map((x, idx) =>
                          idx === ci
                            ? {
                                ...x,
                                services: x.services.map((sv, sidx) =>
                                  sidx === si
                                    ? { ...sv, title: e.target.value }
                                    : sv,
                                ),
                              }
                            : x,
                        ),
                      )
                    }
                  />
                </Field>
                <Field label="Link (href)">
                  <TextInput
                    value={svc.href}
                    onChange={(e) =>
                      setServiceCategories((s) =>
                        s.map((x, idx) =>
                          idx === ci
                            ? {
                                ...x,
                                services: x.services.map((sv, sidx) =>
                                  sidx === si
                                    ? { ...sv, href: e.target.value }
                                    : sv,
                                ),
                              }
                            : x,
                        ),
                      )
                    }
                  />
                </Field>
              </div>
            ))}
          </div>
        ))}
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="CTA Text">
            <TextInput
              value={coreCtaText}
              onChange={(e) => setCoreCtaText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={coreCtaLink}
              onChange={(e) => setCoreCtaLink(e.target.value)}
            />
          </Field>
        </div>
      </CollapsibleSection>

      {/* HOW WE HELP */}
      <CollapsibleSection
        title="How We Help"
        active={howActive}
        onToggleActive={setHowActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={howBadgeText}
            onChange={(e) => setHowBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={howHeading}
            onChange={(e) => setHowHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={howHeadingEmphasis}
            onChange={(e) => setHowHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={howDescription}
            onChange={(e) => setHowDescription(e.target.value)}
          />
        </Field>

        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Cards</p>
          <button
            onClick={() =>
              setHowCards((s) => [...s, { title: "", description: "" }])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Card
          </button>
        </div>
        {howCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Card {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={howCards.length}
                  onMove={(f, t) => setHowCards(moveItem(howCards, f, t))}
                />
                {howCards.length > 1 && (
                  <button
                    onClick={() =>
                      setHowCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setHowCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setHowCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="CTA Text">
            <TextInput
              value={howCtaText}
              onChange={(e) => setHowCtaText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={howCtaLink}
              onChange={(e) => setHowCtaLink(e.target.value)}
            />
          </Field>
        </div>
      </CollapsibleSection>

      {/* TECHNOLOGY */}
      <CollapsibleSection
        title="Technology"
        active={techActive}
        onToggleActive={setTechActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={techBadgeText}
            onChange={(e) => setTechBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={techHeading}
            onChange={(e) => setTechHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={techHeadingEmphasis}
            onChange={(e) => setTechHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={techDescription}
            onChange={(e) => setTechDescription(e.target.value)}
          />
        </Field>

        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Integration Pills</p>
          <button
            onClick={() => setTechIntegrations((s) => [...s, ""])}
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Integration
          </button>
        </div>
        {techIntegrations.map((item, i) => (
          <div key={i} className="flex items-center gap-2 mb-2">
            <TextInput
              value={item}
              onChange={(e) =>
                setTechIntegrations((s) =>
                  s.map((x, idx) => (idx === i ? e.target.value : x)),
                )
              }
            />
            {techIntegrations.length > 1 && (
              <button
                onClick={() =>
                  setTechIntegrations((s) => s.filter((_, idx) => idx !== i))
                }
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="CTA Text">
            <TextInput
              value={techCtaText}
              onChange={(e) => setTechCtaText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={techCtaLink}
              onChange={(e) => setTechCtaLink(e.target.value)}
            />
          </Field>
        </div>
      </CollapsibleSection>

      {/* CLIENT SUCCESS */}
      <CollapsibleSection
        title="Client Success"
        active={successActive}
        onToggleActive={setSuccessActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={successBadgeText}
            onChange={(e) => setSuccessBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={successHeading}
            onChange={(e) => setSuccessHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={successHeadingEmphasis}
            onChange={(e) => setSuccessHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Quote">
          <TextArea
            value={successQuote}
            onChange={(e) => setSuccessQuote(e.target.value)}
          />
        </Field>
        <Field label="Author">
          <TextInput
            value={successAuthor}
            onChange={(e) => setSuccessAuthor(e.target.value)}
          />
        </Field>

        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">KPI Pills</p>
          <button
            onClick={() => setSuccessKpis((s) => [...s, ""])}
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add KPI
          </button>
        </div>
        {successKpis.map((kpi, i) => (
          <div key={i} className="flex items-center gap-2 mb-2">
            <TextInput
              value={kpi}
              onChange={(e) =>
                setSuccessKpis((s) =>
                  s.map((x, idx) => (idx === i ? e.target.value : x)),
                )
              }
            />
            {successKpis.length > 1 && (
              <button
                onClick={() =>
                  setSuccessKpis((s) => s.filter((_, idx) => idx !== i))
                }
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="CTA Text">
            <TextInput
              value={successCtaText}
              onChange={(e) => setSuccessCtaText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={successCtaLink}
              onChange={(e) => setSuccessCtaLink(e.target.value)}
            />
          </Field>
        </div>
      </CollapsibleSection>

      {/* WHY THYNKPROCESS */}
      <CollapsibleSection
        title="Why ThynkProcess"
        active={whyActive}
        onToggleActive={setWhyActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={whyBadgeText}
            onChange={(e) => setWhyBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={whyHeading}
            onChange={(e) => setWhyHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={whyHeadingEmphasis}
            onChange={(e) => setWhyHeadingEmphasis(e.target.value)}
          />
        </Field>

        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Cards</p>
          <button
            onClick={() =>
              setWhyCards((s) => [...s, { title: "", description: "" }])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Card
          </button>
        </div>
        {whyCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Card {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={whyCards.length}
                  onMove={(f, t) => setWhyCards(moveItem(whyCards, f, t))}
                />
                {whyCards.length > 1 && (
                  <button
                    onClick={() =>
                      setWhyCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setWhyCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setWhyCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* FINAL CTA */}
      <CollapsibleSection
        title="Final CTA"
        active={ctaActive}
        onToggleActive={setCtaActive}
      >
        <Field label="Heading">
          <TextInput
            value={ctaHeading}
            onChange={(e) => setCtaHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={ctaDescription}
            onChange={(e) => setCtaDescription(e.target.value)}
          />
        </Field>
        <Field label="Contact Line">
          <TextInput
            value={ctaContact}
            onChange={(e) => setCtaContact(e.target.value)}
          />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={ctaPrimary.text}
          url={ctaPrimary.url}
          newTab={ctaPrimary.newTab}
          onTextChange={(v) => updateBtn(setCtaPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaPrimary, "newTab", v)}
        />
        <CTAGroup
          label="Secondary CTA"
          text={ctaSecondary.text}
          url={ctaSecondary.url}
          newTab={ctaSecondary.newTab}
          onTextChange={(v) => updateBtn(setCtaSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaSecondary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaSecondary, "newTab", v)}
        />
      </CollapsibleSection>
    </>
  );
}
